import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Calendar, Languages } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { CATEGORIES, langByCode } from "../lib/blogHelpers";
import { logError } from "../lib/logger";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogIndex() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("all");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/blogs`);
        setBlogs(data || []);
      } catch (e) {
        logError("Failed to load blogs:", e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered =
    activeCategory === "all"
      ? blogs
      : blogs.filter((b) => b.category === activeCategory);

  return (
    <div data-testid="page-blogs">
      <PageHeader
        eyebrow="Blogs"
        title="Longer thoughts, slowly written."
        intro="Pieces that wouldn't fit in a paragraph elsewhere — full stories from the trails, and reflections on everything else. Most posts are written in both English and Bengali — you can switch language at the top of each piece."
        testid="blogs-header"
      />

      <section className="pb-24">
        <div className="container-narrow">
          <div className="flex flex-wrap gap-2 mb-10" data-testid="blog-category-filter">
            <button
              type="button"
              onClick={() => setActiveCategory("all")}
              className={`px-4 py-2 rounded-sm text-sm tracking-wide border transition-all duration-300 ${
                activeCategory === "all"
                  ? "bg-[#4A6B53] text-[#FAF9F6] border-[#4A6B53]"
                  : "bg-transparent text-[#2C2A28] border-[#D8D3CC] hover:border-[#4A6B53] hover:text-[#4A6B53]"
              }`}
              data-testid="blog-filter-all"
            >
              All
            </button>
            {CATEGORIES.map((c) => (
              <button
                type="button"
                key={c.code}
                onClick={() => setActiveCategory(c.code)}
                className={`px-4 py-2 rounded-sm text-sm tracking-wide border transition-all duration-300 ${
                  activeCategory === c.code
                    ? "bg-[#4A6B53] text-[#FAF9F6] border-[#4A6B53]"
                    : "bg-transparent text-[#2C2A28] border-[#D8D3CC] hover:border-[#4A6B53] hover:text-[#4A6B53]"
                }`}
                data-testid={`blog-filter-${c.code}`}
              >
                {c.name}
              </button>
            ))}
          </div>

          {loading && (
            <p className="text-[#5C5852] italic" data-testid="blogs-loading">
              Loading…
            </p>
          )}

          {!loading && filtered.length === 0 && (
            <div
              className="rounded-sm bg-[#F2EFEB] border border-[#D8D3CC] p-10 text-center"
              data-testid="blogs-empty"
            >
              <p className="font-serif text-2xl text-[#2C2A28]">
                No posts here yet.
              </p>
              <p className="mt-2 text-sm text-[#5C5852]">
                The pen is being sharpened. Check back soon.
              </p>
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-6" data-testid="blog-list">
            {filtered.map((b, i) => {
              const langs = Object.keys(b.translations || {});
              const primaryLang = langs.includes("en") ? "en" : langs[0];
              const tr = b.translations?.[primaryLang] || {};
              const cat = CATEGORIES.find((c) => c.code === b.category);
              return (
                <motion.article
                  key={b.id}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.5, delay: i * 0.05 }}
                  className="card-lift rounded-sm bg-[#F2EFEB] border border-[#D8D3CC] overflow-hidden flex flex-col group"
                  data-testid={`blog-card-${b.slug}`}
                >
                  <Link to={`/blogs/${b.slug}`} className="flex flex-col h-full">
                    {b.coverImage && (
                      <div className="relative aspect-[16/10] overflow-hidden">
                        <img
                          src={b.coverImage}
                          alt={tr.title || "Blog cover"}
                          loading="lazy"
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                        />
                        {cat && (
                          <div className="absolute top-3 left-3 inline-flex items-center bg-[#FAF9F6]/90 backdrop-blur px-2.5 py-1 rounded-sm text-[11px] tracking-wider text-[#2C2A28] uppercase">
                            {cat.name}
                          </div>
                        )}
                      </div>
                    )}
                    <div className="p-6 sm:p-7 flex flex-col flex-1">
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-[#5C5852]">
                        {b.publishedAt && (
                          <span className="inline-flex items-center gap-1.5">
                            <Calendar size={12} strokeWidth={1.6} className="text-[#4A6B53]" />
                            {new Date(b.publishedAt).toLocaleDateString(undefined, {
                              day: "numeric",
                              month: "short",
                              year: "numeric",
                            })}
                          </span>
                        )}
                        {langs.length > 1 && (
                          <span className="inline-flex items-center gap-1.5">
                            <Languages size={12} strokeWidth={1.6} className="text-[#4A6B53]" />
                            {langs.map((l) => langByCode(l).native).join(" · ")}
                          </span>
                        )}
                      </div>
                      <h3 className="font-serif text-2xl sm:text-3xl mt-3 text-[#2C2A28] leading-tight">
                        {tr.title || "Untitled"}
                      </h3>
                      <p
                        className="mt-2 text-sm text-[#5C5852]"
                        data-testid={`blog-card-author-${b.slug}`}
                      >
                        Author: {b.authorName || "Rajib Sengupta"}
                      </p>
                      {tr.synopsis && (
                        <p className="mt-3 text-[#5C5852] leading-relaxed flex-1 line-clamp-4">
                          {tr.synopsis}
                        </p>
                      )}
                      <div className="mt-5 inline-flex items-center gap-1.5 text-[#4A6B53] text-sm tracking-wide group-hover:gap-2.5 transition-all duration-300">
                        Read full story
                        <ArrowUpRight size={14} strokeWidth={1.8} />
                      </div>
                    </div>
                  </Link>
                </motion.article>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
